import {
  Box,
  Field,
  FieldErrorTextProps,
  FieldLabelProps,
  FieldRootProps,
} from '@chakra-ui/react';
import React, { FC, ReactNode, forwardRef } from 'react';
import { MdInfo } from 'react-icons/md';
import { TooltipProps } from '../ui/tooltip';
import { MBox, MFlex, MIcon } from './chakra';
import { MTooltip } from './MTooltip';

export interface MFormFieldProps extends FieldRootProps {
  label?: string;
  labelProps?: FieldLabelProps;
  labelRightComponent?: React.ReactNode;
  skipLabel?: boolean;
  hint?: string;
  isHorizontal?: boolean;
  tooltip?: ReactNode | string;
  tooltipSize?: TooltipProps['size'];
  tooltipColor?: string;
  tooltipUsePopover?: boolean;
  error?: any;
  isReadOnly?: boolean;
  /**
   * v2 name for `required` (the real `FieldRootProps` field, already
   * available via extension above) — kept as its own prop since it's what
   * every existing call site passes. Also drives `Field.RequiredIndicator`,
   * rendered next to the label below.
   */
  isRequired?: boolean;
  /** v2 name for `disabled` (the real `FieldRootProps` field). */
  isDisabled?: boolean;
  /**
   * v2 name for `invalid` (the real `FieldRootProps` field). Most callers
   * rely on `error` below instead (invalid is derived from `!!error?.message`);
   * this is for the few that compute their own invalid state directly.
   */
  isInvalid?: boolean;
  children?: React.ReactNode;
  formErrorMessageProps?: FieldErrorTextProps;
}

const MFormField: FC<MFormFieldProps> = forwardRef<
  HTMLDivElement,
  MFormFieldProps
>(
  (
    {
      label = '',
      labelProps,
      skipLabel = false,
      hint = '',
      isHorizontal = false,
      tooltip,
      tooltipSize,
      tooltipColor = 'tPurple.base',
      tooltipUsePopover = false,
      isReadOnly = false,
      isRequired,
      required,
      isDisabled,
      disabled,
      isInvalid,
      invalid,
      error,
      children,
      formErrorMessageProps,
      labelRightComponent,
      ...rest
    }: MFormFieldProps,
    ref,
  ) => {
    const verticalLabelProps = isReadOnly
      ? { mb: 0, ...labelProps }
      : labelProps;

    if (isHorizontal) {
      return (
        <Field.Root
          ref={ref}
          invalid={invalid ?? isInvalid ?? !!error?.message}
          required={required ?? isRequired}
          disabled={disabled ?? isDisabled}
          display="flex"
          flexDirection="row"
          alignItems="center"
          {...rest}
        >
          {!!label && !tooltip && (
            <Field.Label mb="0" display="inline-block" {...labelProps}>
              {label}
              <Field.RequiredIndicator />
            </Field.Label>
          )}
          {!!label && !!tooltip && (
            <MFlex>
              <Field.Label mr="0" display="inline-block" {...labelProps}>
                {label}
                <Field.RequiredIndicator />
              </Field.Label>
              <MTooltip
                label={tooltip}
                placement="bottom-start"
                size={tooltipSize}
                usePopover={tooltipUsePopover}
              >
                <MFlex alignItems="center">
                  <MIcon as={MdInfo} color="tPurple.base" mx="2" />
                </MFlex>
              </MTooltip>
            </MFlex>
          )}
          <MBox flex="1">
            {children}
            {!!error && (
              <Field.ErrorText
                color="tRed.base"
                fontSize="sm"
                {...formErrorMessageProps}
              >
                {error?.message}
              </Field.ErrorText>
            )}
            {!!hint && <Field.HelperText>{hint}</Field.HelperText>}
          </MBox>
        </Field.Root>
      );
    }

    return (
      <Field.Root
        ref={ref}
        invalid={invalid ?? isInvalid ?? !!error?.message}
        required={required ?? isRequired}
        disabled={disabled ?? isDisabled}
        {...rest}
      >
        {!!label && !tooltip && (
          <MFlex justify="space-between" align="center">
            <Field.Label mr="0" display="inline-block" {...verticalLabelProps}>
              {label}
              <Field.RequiredIndicator />
            </Field.Label>
            {labelRightComponent}
          </MFlex>
        )}
        {!!label && !!tooltip && (
          <MFlex>
            <MFlex justify="space-between" align="center">
              <MFlex align="center">
                <Field.Label
                  mr="0"
                  display="inline-block"
                  {...verticalLabelProps}
                >
                  {label}
                  <Field.RequiredIndicator />
                </Field.Label>
                <MTooltip
                  label={tooltip}
                  placement="bottom-start"
                  usePopover={tooltipUsePopover}
                >
                  <MBox mt="-1.5">
                    <MIcon as={MdInfo} color={tooltipColor} ml="1" />
                  </MBox>
                </MTooltip>
              </MFlex>
              {labelRightComponent}
            </MFlex>
          </MFlex>
        )}
        {!label && !!skipLabel && <Box mt="7" />}
        {children}
        {!!error && (
          <Field.ErrorText
            color="tRed.base"
            fontSize="sm"
            {...formErrorMessageProps}
          >
            {error?.message}
          </Field.ErrorText>
        )}
        {!!hint && <Field.HelperText>{hint}</Field.HelperText>}
      </Field.Root>
    );
  },
);

export default MFormField;
